import React, { useEffect } from 'react'
import { Layout, App as AntApp, Modal, Button, Typography } from 'antd'
import { Outlet } from 'react-router-dom'
import { useApi } from '../services/ApiContext'
import { SprintSpecIcon } from '../components/Logo/SprintSpecIcon'
import { useCompanyStore } from '../store/company/companyStore'
import Intercom from '@intercom/messenger-js-sdk'
import { useTrialStatus } from '../hooks/useTrialStatus'
import { ArrowRight, Check } from 'lucide-react'
import {
  getPricingPlans,
  mapCountryToGeo,
  pricingByGeo,
} from '../data/pricing'
import { CountryCode } from 'countries-and-timezones'

const { Title, Text } = Typography

const TrialExpiredModal: React.FC = () => {
  const { isTrialExpired } = useTrialStatus()
  const api = useApi()
  const company = useCompanyStore((state) => state.company)

  const pricing = pricingByGeo[mapCountryToGeo(company.country as CountryCode)]
  const monthlyPlan = getPricingPlans('monthly')[0]
  const yearlyPlan = getPricingPlans('yearly')[0]
  const features = monthlyPlan.features
    .filter((f) => f.included)
    .map((f) => f.name)

  const handleUpgrade = async (billingPeriod: 'monthly' | 'yearly') => {
    const { url } = await api.createCheckoutSession(billingPeriod)
    window.location.href = url
  }

  return (
    <Modal
      open={isTrialExpired}
      closable={false}
      footer={null}
      width={720}
      maskClosable={false}
      centered
      styles={{ body: { padding: '32px 24px' } }}
    >
      <div className="text-center mb-6">
        <SprintSpecIcon className="h-10 w-10 mx-auto mb-4 text-primary" />
        <Title level={3} className="mb-2">
          Your free trial has ended
        </Title>
        <Text className="text-gray-500 text-base">
          Upgrade to continue using SprintSpec and unlock all features.
        </Text>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Yearly */}
        <div className="relative flex flex-col rounded-2xl border border-primary bg-white p-6 shadow-md">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="rounded-full bg-primary px-4 py-1 text-xs font-semibold text-white">
              Recommended
            </span>
          </div>
          <Title level={4} className="mb-1">
            Yearly
          </Title>
          <Text className="mb-4 block text-gray-500 text-sm">
            Save 20% with annual billing
          </Text>
          <div className="mb-4">
            <span className="text-4xl font-bold tracking-tight">
              {pricing.currencySymbol}
              {yearlyPlan.price}
            </span>
            <Text type="secondary" className="ml-1">
              /mo
            </Text>
          </div>
          <Button
            type="primary"
            size="large"
            className="mb-4 w-full"
            onClick={() => handleUpgrade('yearly')}
          >
            <span className="flex items-center justify-center gap-2">
              Upgrade Now
              <ArrowRight className="h-4 w-4" />
            </span>
          </Button>
          <div className="space-y-2">
            {features.slice(0, 5).map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                <Text className="text-xs">{feature}</Text>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly */}
        <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <Title level={4} className="mb-1">
            Monthly
          </Title>
          <Text className="mb-4 block text-gray-500 text-sm">
            Flexible month-to-month billing
          </Text>
          <div className="mb-4">
            <span className="text-4xl font-bold tracking-tight">
              {pricing.currencySymbol}
              {monthlyPlan.price}
            </span>
            <Text type="secondary" className="ml-1">
              /mo
            </Text>
          </div>
          <Button
            size="large"
            className="mb-4 w-full"
            onClick={() => handleUpgrade('monthly')}
          >
            <span className="flex items-center justify-center gap-2">
              Upgrade Now
              <ArrowRight className="h-4 w-4" />
            </span>
          </Button>
          <div className="space-y-2">
            {features.slice(0, 5).map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                <Text className="text-xs">{feature}</Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export const InAppLayout = () => {
  const api = useApi()
  const [loading, setLoading] = React.useState(true)
  // const userId = useCompanyStore((state) => state.user?._id)
  const user = useCompanyStore((state) => state.user)
  const company = useCompanyStore((state) => state.company)
  // const navigate = useNavigate()

  useEffect(() => {
    if (!user) return
    Intercom({
      app_id: 'zvuowdiw',
      user_id: user._id,
      name: user.firstName + ' ' + user.lastName,
      email: user.email,
      created_at: new Date(user.createdAt).getTime() / 1000,
      company: {
        id: company._id,
        name: company.name,
      },
    })
  }, [user])

  useEffect(() => {
    reAuth()
  }, [])

  const reAuth = async () => {
    setLoading(true)
    await api.reAuthenticate()
    setLoading(false)
  }

  return (
    <AntApp>
      <Layout className="min-h-screen">
        {/* Full screen loading spinner with logo */}
        {loading && (
          <div
            style={{ zIndex: 10000 }}
            className="fixed inset-0 flashy-bg flex items-center flex-col justify-center"
          >
            <SprintSpecIcon className="text-white h-32 w-32" />
            {/* <Spin className='mt-6' spinning indicator={<LoadingOutlined spin style={{color: '#fff'}} />} size="large" /> */}
          </div>
        )}
        {user && (
          <>
            <TrialExpiredModal />
            <Outlet />
          </>
        )}
      </Layout>
    </AntApp>
  )
}
